// pages/SearchResultPage.tsx
// 新 PREFIX_SEARCH API 移行版

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildPrefixSearchUrl } from '../config/api';

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { PrefixSearchBook, PrefixSearchAuthor } from '../types/rlTypes';
import { useAppTokens } from '../context/ThemeModeContext';

const SearchResultPage: React.FC = () => {
  const { query } = useParams<{ query: string }>();
  const [bookResults, setBookResults] = useState<PrefixSearchBook[]>([]);
  const [authorResults, setAuthorResults] = useState<PrefixSearchAuthor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [bookNextCursor, setBookNextCursor] = useState<string | null>(null);
  const [loadingMoreBooks, setLoadingMoreBooks] = useState<boolean>(false);
  const navigate = useNavigate();

  const TOKENS = useAppTokens();

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    boxShadow: '0 0 0 rgba(0,0,0,0)',
    backdropFilter: 'blur(6px)',
  } as const;

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: 1.25,
    py: 0.75,
    borderRadius: 999,
    background: TOKENS.glassStrong,
    border: `1px solid ${TOKENS.border}`,
    color: TOKENS.text,
    fontSize: 12,
    lineHeight: 1,
    userSelect: 'none',
  } as const;

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query) return;
      setLoading(true);
      try {
        const response = await axios.get(buildPrefixSearchUrl(query, 400, 100));
        const data = response.data;

        setBookResults(data.books ?? []);
        setAuthorResults(data.authors ?? []);
        setBookNextCursor(data.book_next_cursor ?? null);
      } catch (error) {
        console.error('Error fetching search results:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [query]);

  // 書籍の「もっと見る」。クエリの文字種を問わずbookNextCursorが発行される
  // （1回で追加取得できる件数は文字種によって変わる）
  const handleLoadMoreBooks = async () => {
    if (!query || !bookNextCursor || loadingMoreBooks) return;
    setLoadingMoreBooks(true);
    try {
      const response = await axios.get(buildPrefixSearchUrl(query, 400, 0, bookNextCursor));
      const data = response.data;

      setBookResults((prev) => [...prev, ...(data.books ?? [])]);
      setBookNextCursor(data.book_next_cursor ?? null);
    } catch (error) {
      console.error('Error fetching more search results:', error);
    } finally {
      setLoadingMoreBooks(false);
    }
  };

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const bgSx = {
    width: '100%',
    minHeight: 'calc(100dvh - 56px)',
    background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
    color: TOKENS.text,
    position: 'relative',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  } as const;

  return (
    <Box sx={{ mt: 7 }}>
      <Box sx={bgSx}>
        {/* pills */}
        <Box
          sx={{
            position: 'absolute', top: 14, left: 14, zIndex: 5,
            display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', pointerEvents: 'none',
          }}
        >
          <Box sx={pillSx}>検索結果</Box>
          {query && <Box sx={{ ...pillSx, opacity: 0.85 }}>「{query}」</Box>}
        </Box>

        <Container maxWidth="lg" sx={{ pt: 8, pb: 4, px: { xs: 2, md: 3 } }}>
          {/* tabs */}
          <Box sx={{ ...glassPanelSx, mb: 2 }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                minHeight: 44,
                '& .MuiTabs-indicator': { backgroundColor: TOKENS.accent },
                '& .MuiTab-root': { minHeight: 44, color: TOKENS.textSub, textTransform: 'none', fontSize: 13 },
                '& .Mui-selected': { color: TOKENS.text },
              }}
            >
              <Tab label="本" />
              <Tab label="著者" />
            </Tabs>
          </Box>

          {loading ? (
            <Box sx={{ ...glassPanelSx, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, minHeight: '60vh' }}>
              <CircularProgress />
              <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>検索中...</Typography>
            </Box>
          ) : tabIndex === 0 ? (
            bookResults.length === 0 ? (
              <Box sx={{ ...glassPanelSx, p: 3 }}>
                <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>検索結果が0件です。</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ ...glassPanelSx, p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                    本の検索結果：{bookResults.length}件
                  </Typography>
                </Box>

                <Grid container spacing={1.8}>
                  {bookResults.map((result) => (
                    <Grid item xs={6} sm={4} md={3} key={result.rl_book_id}>
                      <Box>
                        <Card
                          onClick={() => {
                            navigate(`/book/${result.rl_book_id}`);
                            window.scrollTo(0, 0);
                          }}
                          sx={{
                            transition: '0.25s',
                            background: TOKENS.cardBg,
                            border: `1px solid ${TOKENS.border}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxShadow: 'none',
                            '&:hover': {
                              border: `1px solid ${TOKENS.accent}`,
                              background: TOKENS.cardBgHover,
                            },
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={generateCoverUrlSmart(result.rl_book_id, result.title, result.isbn13)}
                            alt={`Cover for ${result.title}`}
                            sx={{
                              objectFit: 'contain',
                              height: { xs: 170, sm: 190 },
                              width: '100%',
                              padding: '10px',
                              background: TOKENS.cardBg,
                            }}
                          />
                        </Card>

                        <Box sx={{ mt: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 12.5, color: TOKENS.text, lineHeight: 1.25,
                              display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden',
                            }}
                          >
                            {result.title}
                          </Typography>

                          {result.responsibility && (
                            <Typography
                              sx={{
                                fontSize: 12, color: TOKENS.textSub, mt: 0.25,
                                display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 1, overflow: 'hidden',
                              }}
                            >
                              {result.responsibility}
                            </Typography>
                          )}

                          {result.publishing_date && (
                            <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.25 }}>
                              {result.publishing_date}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {bookNextCursor && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Button
                      onClick={handleLoadMoreBooks}
                      disabled={loadingMoreBooks}
                      variant="outlined"
                      sx={{
                        color: TOKENS.text,
                        borderColor: TOKENS.border,
                        textTransform: 'none',
                        '&:hover': { borderColor: TOKENS.accent, background: TOKENS.cardBgHover },
                      }}
                    >
                      {loadingMoreBooks ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                      もっと見る
                    </Button>
                  </Box>
                )}
              </>
            )
          ) : tabIndex === 1 ? (
            authorResults.length === 0 ? (
              <Box sx={{ ...glassPanelSx, p: 3 }}>
                <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>検索結果が0件です。</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ ...glassPanelSx, p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                    著者の検索結果：{authorResults.length}件
                  </Typography>
                  <Box sx={{ ...pillSx, opacity: 0.85 }}>スコア順</Box>
                </Box>

                <Box sx={{ ...glassPanelSx, overflow: 'hidden' }}>
                  <Stack spacing={0}>
                    {authorResults.map((author) => (
                      <Box
                        key={author.tenkyo_id}
                        onClick={() => navigate(`/author/${author.tenkyo_id}`)}
                        sx={{
                          px: 2, py: 1.5,
                          borderBottom: `1px solid ${TOKENS.border}`,
                          cursor: 'pointer', transition: '0.2s',
                          '&:hover': { background: TOKENS.cardBgHover },
                        }}
                      >
                        <Typography sx={{ fontSize: 13.5, color: TOKENS.text }}>
                          {author.name} {author.birth_death}
                        </Typography>

                        {author.alternative_names && author.alternative_names.length > 0 && (
                          <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.25 }}>
                            別名: {author.alternative_names.slice(0, 3).join(' ')}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </>
            )
          ) : null}
        </Container>
      </Box>
    </Box>
  );
};

export default SearchResultPage;
